import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { AngleUp } from '@components/icons/arrow/arrow';
import EducationArrowLink from '@components/atoms/education/arrow-link/educationArrowLink';
import MixedCopy from '@components/molecules/mixed-copy/mixedCopy';
import { InstantSearch, Configure } from 'react-instantsearch-dom';
import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';
import CustomInfiniteHits from '@components/atoms/education/refinement-hits/educationRefinementHits';
import EducationCategoryTeaser from '../category-teaser/educationCategoryTeaser';
import StyledEducationStartStage from './educationStartStage.style';
import Container from '../../../layout/container/container';
import Headline from '../../../atoms/headline/headline';

const typesenseInstantSearchAdapter = new TypesenseInstantsearchAdapter({
  server: {
    apiKey: process.env.NEXT_PUBLIC_TYPESENSE_API_KEY,
    nodes: [
      {
        host: process.env.NEXT_PUBLIC_TYPESENSE_HOST,
        port: process.env.NEXT_PUBLIC_TYPESENSE_PORT,
        protocol: process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL
      }
    ]
  },
  additionalSearchParameters: {
    query_by: 'topicFilter',
    query_by_weights: '6'
  }
});

function EducationStartStage({
  lang,
  headline,
  subline,
  introCopy,
  introVideo,
  topicTexts = [],
  setDarkHeader,
  darkHeader
}) {
  const indexName = lang === 'en' ? 'futurium_education_en' : 'futurium_education_de';
  const [topicFiltering, setTopicFiltering] = useState('');

  const setFilter = `topicFilter:${topicFiltering}`;

  const isDarkContent =
    topicFiltering === 'work' ||
    topicFiltering === 'energy' ||
    topicFiltering === 'nutrition' ||
    topicFiltering === 'city' ||
    topicFiltering === 'democracy';

  setDarkHeader(isDarkContent);

  return (
    <StyledEducationStartStage category={topicFiltering}>
      <InstantSearch
        searchClient={typesenseInstantSearchAdapter.searchClient}
        indexName={indexName}
      >
        <Configure filters={`${topicFiltering !== '' ? setFilter : ''}`} hitsPerPage={7} />
        <div className="start-stage__container">
          <div className="start-stage__bg" />
          <div className="start-stage__bg-front" />
          {introVideo && (
            <video className="start-stage__video" playsInline autoPlay muted loop>
              <source type={introVideo.mimeType} src={introVideo.src} />
            </video>
          )}
          <Container large>
            <div
              className={`start-stage__content ${darkHeader ? 'content--light' : 'content--dark'}`}
            >
              <Headline tag="h1">
                {headline && <span className="small">{headline}</span>}
                {subline && <span className="big">{subline}</span>}
              </Headline>
              {introCopy && <MixedCopy fullWidth className="start-stage__copy" html={introCopy} />}
            </div>
          </Container>
        </div>
        <div className="start-stage__teaser">
          <EducationCategoryTeaser
            filter
            topicFiltering={topicFiltering}
            setTopicFiltering={setTopicFiltering}
            topicTexts={topicTexts}
            onlyTopics
          />
          {topicFiltering === '' && (
            <div className="start-stage__teaser-select-topic">
              <AngleUp />
              <Headline tag="span">Wähle ein Thema aus</Headline>
            </div>
          )}
        </div>
        <Container large>
          <div className="start-stage__list">
            <div className="start-stage__list-hero">
              <Headline tag="h2">Ausgewählte Inhalte</Headline>
              <EducationArrowLink
                href={
                  topicFiltering
                    ? `/de/bildung/bibliothek?topicFilter=${topicFiltering}`
                    : `/de/bildung/bibliothek`
                }
              >
                <span className="hidden--mobile">Zur Bibliothek</span>
                <span className="hidden--desktop">Alles</span>
              </EducationArrowLink>
            </div>
            <CustomInfiniteHits disableClearFilter />
          </div>
        </Container>
      </InstantSearch>
    </StyledEducationStartStage>
  );
}

EducationStartStage.propTypes = {
  lang: PropTypes.string.isRequired,
  headline: PropTypes.string,
  subline: PropTypes.string,
  introCopy: PropTypes.string,
  introVideo: PropTypes.shape({
    src: PropTypes.string,
    title: PropTypes.string,
    mimeType: PropTypes.string
  }),
  topicTexts: PropTypes.shape({
    subline: PropTypes.string,
    introCopy: PropTypes.string
  }),
  setDarkHeader: PropTypes.func.isRequired,
  darkHeader: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
  lang: state.app.pageData.lang,
  headline: state.app.pageData.headline,
  subline: state.app.pageData.subline,
  introCopy: state.app.pageData.introCopy,
  introVideo: state.app.pageData.introVideo,
  topicTexts: state.app.pageData.educationTopicTexts
});

export default connect(mapStateToProps)(EducationStartStage);
